const getBaseUrl = () =>
  (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(
    /\/+$/,
    ""
  );

const getAuthToken = () =>
  localStorage.getItem("authToken") || localStorage.getItem("token") || "";

export const getTenantPlanId = (plan) =>
  Number(
    plan?.planId ||
      plan?.planid ||
      plan?.PlanId ||
      plan?.PlanID ||
      0
  );

export const FREE_PLAN_ID = 1;

export const getIsLastTime = (plan) =>
  Number(plan?.isLastTime ?? plan?.IsLastTime ?? 0);

export const isOfferedPlan = (plan) => getIsLastTime(plan) !== 1;

export const isFreeCatalogPlanId = (plan) => getTenantPlanId(plan) === FREE_PLAN_ID;

const MONTHS = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

const parseApiDate = (value) => {
  if (value == null || value === "" || value === "undefined") return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime()) || value.getFullYear() < 2000) return null;
    return value;
  }

  const text = String(value).replace(/\s+/g, " ").trim();
  // صيغة الـ API: "Aug 15 2026  2:07PM"
  const match = text.match(
    /^([A-Za-z]{3}) (\d{1,2}) (\d{4}) (\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i
  );

  let date = null;
  if (match) {
    const month = MONTHS[match[1].toLowerCase()];
    if (month != null) {
      let hour = Number(match[4]);
      const minute = Number(match[5]);
      const second = Number(match[6] || 0);
      const ampm = (match[7] || "").toUpperCase();
      if (ampm === "PM" && hour < 12) hour += 12;
      if (ampm === "AM" && hour === 12) hour = 0;
      date = new Date(Number(match[3]), month, Number(match[2]), hour, minute, second);
    }
  }

  if (!date || Number.isNaN(date.getTime())) {
    date = new Date(text.replace(/(\d)(AM|PM)$/i, "$1 $2"));
  }

  if (Number.isNaN(date.getTime()) || date.getFullYear() < 2000) return null;
  return date;
};

const asPlanText = (value) => {
  if (value == null || typeof value === "object") return "";
  return String(value);
};

const getPlanText = (plan) =>
  [
    asPlanText(plan?.planType),
    asPlanText(plan?.engPlanType),
    asPlanText(plan?.title),
    asPlanText(plan?.engTitle),
    asPlanText(plan?.descripsion),
    asPlanText(plan?.note1),
  ].join(" ");

export const getPlanStartDate = (plan) =>
  parseApiDate(plan?.creationDate || plan?.CreationDate);

export const isFreeOrTrialPlan = (plan) => {
  if (!plan) return true;
  const price = Number(plan.price ?? plan.Price ?? 0);
  const type = getPlanText(plan).toLowerCase();
  return price === 0 || type.includes("free") || type.includes("trial") || type.includes("مجان");
};

export const getPlanDurationDays = (plan) => {
  if (isFreeOrTrialPlan(plan)) return 7;
  const type = getPlanText(plan).toLowerCase();
  if (type.includes("week") || type.includes("أسبوع")) return 7;
  if (type.includes("year") || type.includes("سنة") || type.includes("عام")) return 365;
  if (type.includes("month") || type.includes("شهر")) return 30;
  return 30;
};

/** تاريخ الانتهاء = creationDate من GetTenantPlan + مدة الخطة */
export const getPlanExpiryDate = (plan) => {
  const startDate = getPlanStartDate(plan);
  if (!startDate) return null;

  return new Date(
    startDate.getTime() + getPlanDurationDays(plan) * 24 * 60 * 60 * 1000
  );
};

/**
 * صلاحية الخطة (مدفوعة أو مجانية) تُقارن فقط بـ creationDate من GetTenantPlan.
 */
export const isPlanExpired = (plan) => {
  if (!plan) return true;

  const expiryDate = getPlanExpiryDate(plan);
  if (!expiryDate) return true;

  return Date.now() > expiryDate.getTime();
};

const hasTenantPlanShape = (plan) =>
  !!(
    getTenantPlanId(plan) ||
    plan?.creationDate ||
    plan?.CreationDate ||
    plan?.planType ||
    plan?.PlanType
  );

export const asTenantPlanList = (data) => {
  if (Array.isArray(data)) return data.filter(Boolean);
  if (data && typeof data === "object" && hasTenantPlanShape(data)) return [data];
  return [];
};

export const pickTenantPlanFromResponse = (data) => {
  const list = asTenantPlanList(data);
  if (list.length === 0) return null;
  const current = list.filter(isOfferedPlan);
  return current[0] || list[0] || null;
};

/** بعد استخدام التجربة المجانية (IsLastTime=1 أو انتهاء planId=1) لا تُعرض مرة أخرى */
export const shouldHideFreePlanFromCatalog = (tenantPlans) =>
  asTenantPlanList(tenantPlans).some((plan) => {
    if (!isFreeCatalogPlanId(plan)) return false;
    return getIsLastTime(plan) === 1 || isPlanExpired(plan);
  });

export const filterCatalogPlansForUser = (catalogPlans, tenantPlans) => {
  const list = Array.isArray(catalogPlans) ? catalogPlans : [];
  const hideFreePlan = shouldHideFreePlanFromCatalog(tenantPlans);

  return list.filter((plan) => {
    if (Number(plan?.isActive ?? 1) !== 1) return false;
    if (hideFreePlan && isFreeCatalogPlanId(plan)) return false;
    return true;
  });
};

let tenantPlanCache = {
  tenantId: null,
  plan: undefined,
  plans: undefined,
  fetchedAt: 0,
  inFlight: null,
};

const CACHE_TTL_MS = 30 * 1000;

export const invalidateTenantPlanCache = () => {
  tenantPlanCache = {
    tenantId: null,
    plan: undefined,
    plans: undefined,
    fetchedAt: 0,
    inFlight: null,
  };
};

const loadTenantPlanPayload = async () => {
  const tenantId = localStorage.getItem("tenantId");
  if (!tenantId || tenantId === "-1") return { plan: null, plans: [] };

  const now = Date.now();
  if (
    tenantPlanCache.tenantId === tenantId &&
    tenantPlanCache.plans !== undefined &&
    now - tenantPlanCache.fetchedAt < CACHE_TTL_MS
  ) {
    return { plan: tenantPlanCache.plan, plans: tenantPlanCache.plans };
  }

  if (tenantPlanCache.inFlight && tenantPlanCache.tenantId === tenantId) {
    return tenantPlanCache.inFlight;
  }

  const request = (async () => {
    const res = await fetch(
      `${getBaseUrl()}/Plans/GetTenantPlan?TenantId=${tenantId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: getAuthToken() ? `Bearer ${getAuthToken()}` : "",
        },
      }
    );

    if (res.status === 404) return { plan: null, plans: [] };
    if (!res.ok) throw new Error(`Failed to fetch tenant plan: HTTP ${res.status}`);

    const data = await res.json();
    const plans = asTenantPlanList(data);
    return {
      plan: pickTenantPlanFromResponse(plans),
      plans,
    };
  })();

  tenantPlanCache.tenantId = tenantId;
  tenantPlanCache.inFlight = request;

  try {
    const payload = await request;
    tenantPlanCache.plan = payload.plan;
    tenantPlanCache.plans = payload.plans;
    tenantPlanCache.fetchedAt = Date.now();
    tenantPlanCache.inFlight = null;
    return payload;
  } catch (error) {
    tenantPlanCache.inFlight = null;
    throw error;
  }
};

export const fetchCurrentTenantPlan = async ({ force = false } = {}) => {
  if (force) invalidateTenantPlanCache();
  const payload = await loadTenantPlanPayload();
  return payload.plan;
};

export const fetchTenantPlanRecords = async ({ force = false } = {}) => {
  if (force) invalidateTenantPlanCache();
  const payload = await loadTenantPlanPayload();
  return payload.plans;
};

export const deleteTenantPlanIfExpired = async ({ baseUrl, tenantId, plan, token }) => {
  const planId = getTenantPlanId(plan);
  if (!tenantId || tenantId === -1 || !planId) return false;

  const res = await fetch(
    `${baseUrl}/Plans/DeleteTenantPlan?TenantId=${tenantId}&PlanId=${planId}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
    }
  );

  if (!res.ok && res.status !== 404) {
    const errorData = await res.json().catch(() => ({}));
    const message = errorData.message || errorData.Message || `HTTP ${res.status}`;
    throw new Error(message);
  }

  invalidateTenantPlanCache();
  return true;
};
